import { GROUP_BY_OPTIONS, REPORT_MAX_RANGE_DAYS, type DateRange, type GroupBy } from './types';

/**
 * The shared date range (MASTER_PLAN M11.7, FR-ADMIN-10, PRODUCT.md §9, rules §51.7).
 *
 * Pure functions over `yyyy-mm-dd` strings and an injected clock, with no React and no
 * `Date.now()` — which is banned in this repo's production code for exactly this reason
 * (TESTING.md §2.1). Every preset, every boundary and the 366-day cap are unit-tested
 * here rather than through a rendered screen.
 *
 * **Dates are calendar days, not instants.** A report range is "the 1st to the 30th in
 * the clinic's own time zone", and the server is what resolves that zone
 * (PRODUCT.md §9). Doing the arithmetic on the string in UTC keeps the browser's own
 * offset out of it: adding a day to `2026-03-08` is `2026-03-09` whether or not the
 * reader's clock changed that night.
 */

export const PRESET_IDS = [
  'last-7',
  'last-30',
  'last-90',
  'this-month',
  'last-month',
  'custom',
] as const;

export type PresetId = (typeof PRESET_IDS)[number];

export const PRESET_LABELS: Readonly<Record<PresetId, string>> = {
  'last-7': 'Last 7 days',
  'last-30': 'Last 30 days',
  'last-90': 'Last 90 days',
  'this-month': 'This month',
  'last-month': 'Last month',
  custom: 'Custom range',
};

/**
 * API.md §2.11: `from` and `to` are required and span at most 366 days.
 *
 * The contracts' constant, not a copy of the number: the server refuses a longer range
 * with a `400`, and a client-side cap that had drifted from it would be a control that
 * lets somebody ask for a range nothing can answer.
 */
export const MAX_RANGE_DAYS = REPORT_MAX_RANGE_DAYS;

const DAY_MS = 24 * 3600 * 1000;

/** `yyyy-mm-dd` for a date, read in the browser's own calendar. */
export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** A `yyyy-mm-dd` back to midnight UTC, so arithmetic never crosses a local offset. */
export function fromDateKey(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

export function isDateKey(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(fromDateKey(value).getTime())
  );
}

export function addDays(key: string, days: number): string {
  const shifted = new Date(fromDateKey(key).getTime() + days * DAY_MS);
  return shifted.toISOString().slice(0, 10);
}

/** Inclusive: `2026-06-01` to `2026-06-01` is one day. */
export function daysInRange(range: DateRange): number {
  return (
    Math.round((fromDateKey(range.to).getTime() - fromDateKey(range.from).getTime()) / DAY_MS) + 1
  );
}

/**
 * The range a preset means, measured back from `now`.
 *
 * "Last 7 days" ends **today**, not yesterday: a practice manager who checks the funnel
 * at four in the afternoon wants this afternoon's bookings in it. That the last bucket is
 * a partial day is said on the screen rather than hidden by shifting the window.
 *
 * `custom` has no answer of its own and falls back to the last 30 days, so a URL with
 * `?preset=custom` and no dates still renders something.
 */
export function resolvePreset(preset: PresetId, now: Date): DateRange {
  const today = toDateKey(now);

  switch (preset) {
    case 'last-7':
      return { from: addDays(today, -6), to: today };
    case 'last-90':
      return { from: addDays(today, -89), to: today };
    case 'this-month': {
      const first = `${today.slice(0, 7)}-01`;
      return { from: first, to: today };
    }
    case 'last-month': {
      const firstOfThis = `${today.slice(0, 7)}-01`;
      const lastOfPrevious = addDays(firstOfThis, -1);
      return { from: `${lastOfPrevious.slice(0, 7)}-01`, to: lastOfPrevious };
    }
    case 'last-30':
    case 'custom':
    default:
      return { from: addDays(today, -29), to: today };
  }
}

/**
 * A range the seam is allowed to be asked for.
 *
 * Reversed dates are swapped rather than rejected — somebody typed the second date first,
 * and refusing to draw anything would be a worse answer than drawing what they meant. A
 * span over the cap is trimmed from the **start**, keeping the end the reader chose,
 * because the recent end is what they came for.
 */
export function clampRange(range: DateRange): DateRange {
  const ordered =
    fromDateKey(range.from).getTime() <= fromDateKey(range.to).getTime()
      ? range
      : { from: range.to, to: range.from };

  if (daysInRange(ordered) <= MAX_RANGE_DAYS) return ordered;
  return { from: addDays(ordered.to, -(MAX_RANGE_DAYS - 1)), to: ordered.to };
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function describeDay(key: string): string {
  const month = MONTHS[Number(key.slice(5, 7)) - 1] ?? key.slice(5, 7);
  return `${Number(key.slice(8, 10))} ${month} ${key.slice(0, 4)}`;
}

/**
 * The range in words, for the line every metric carries (rules §51.7).
 *
 * Collapsed where it can be: `1–30 June 2026` rather than `1 June 2026 to 30 June 2026`,
 * and a single day is just that day.
 */
export function describeRange(range: DateRange): string {
  if (range.from === range.to) return describeDay(range.from);

  const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
  const sameMonth = sameYear && range.from.slice(5, 7) === range.to.slice(5, 7);

  if (sameMonth) return `${Number(range.from.slice(8, 10))}–${describeDay(range.to)}`;
  if (sameYear) {
    const fromMonth = MONTHS[Number(range.from.slice(5, 7)) - 1] ?? '';
    return `${Number(range.from.slice(8, 10))} ${fromMonth} – ${describeDay(range.to)}`;
  }
  return `${describeDay(range.from)} – ${describeDay(range.to)}`;
}

const MONTHS_SHORT = MONTHS.map((month) => month.slice(0, 3));

/**
 * A bucket key as an axis label: `15 Sep`, or `w/c 14 Sep` for a week.
 *
 * The generic envelope's buckets arrive with a `label` the server wrote; M5.8's funnel
 * `series[]` carries only the `bucket` date, so the one chart drawn from it formats its
 * own — and formats it the same way, because two charts on one screen labelling the same
 * Tuesday differently is a screen nobody trusts.
 */
export function bucketLabel(key: string, groupBy: GroupBy): string {
  const day = Number(key.slice(8, 10));
  const month = MONTHS_SHORT[Number(key.slice(5, 7)) - 1] ?? key.slice(5, 7);
  if (Number.isNaN(day)) return key;
  return groupBy === 'day' ? `${String(day)} ${month}` : `w/c ${String(day)} ${month}`;
}

/**
 * The grouping a range opens with: days up to five weeks, weeks beyond.
 *
 * Ninety daily bars is a barcode, and five weekly ones over a fortnight throws away the
 * detail somebody opened the report for. The control is still there and still wins — this
 * is only where it starts.
 */
export function defaultGroupBy(range: DateRange): GroupBy {
  return daysInRange(range) > 35 ? 'week' : 'day';
}

export function isGroupBy(value: unknown): value is GroupBy {
  return GROUP_BY_OPTIONS.includes(value as GroupBy);
}

/* ------------------------------------------------------- the route's search */

/**
 * What lives in the query string.
 *
 * The preset rather than the resolved dates, so `/reports/funnel?preset=last-30` is a
 * link that still means "the last 30 days" next week — which is what somebody who pastes
 * it into a message meant. `custom` is the one case that carries its dates, because there
 * is nothing else for it to mean.
 */
export interface ReportSearch {
  preset: PresetId;
  groupBy: GroupBy | 'auto';
  from?: string;
  to?: string;
}

export function parseReportSearch(search: Record<string, unknown>): ReportSearch {
  const preset = PRESET_IDS.includes(search['preset'] as PresetId)
    ? (search['preset'] as PresetId)
    : 'last-30';
  const groupBy = isGroupBy(search['groupBy']) ? search['groupBy'] : 'auto';

  const parsed: ReportSearch = { preset, groupBy };
  if (preset === 'custom' && isDateKey(search['from']) && isDateKey(search['to'])) {
    parsed.from = search['from'];
    parsed.to = search['to'];
  }
  return parsed;
}

/** The range and grouping a search means, resolved against the clock. */
export function resolveSearch(
  search: ReportSearch,
  now: Date,
): { range: DateRange; groupBy: GroupBy } {
  const raw =
    search.preset === 'custom' && isDateKey(search.from) && isDateKey(search.to)
      ? { from: search.from, to: search.to }
      : resolvePreset(search.preset, now);

  const range = clampRange(raw);
  return { range, groupBy: search.groupBy === 'auto' ? defaultGroupBy(range) : search.groupBy };
}
